import { normalizeCurrencyCode } from '@kit/shared/currency';

/**
 * How a date is written, and how typed text is read back.
 *
 * `<input type="date">` displays in the *browser's* locale and offers no way
 * to set that from the page, so a forwarder reading a EUR order on a
 * US-configured laptop saw `03/09/2026` meaning March on a document that says
 * September. No attribute or stylesheet fixes it; the format has to be the
 * page's decision, which is what this module makes it.
 */

/**
 * Currencies whose home market writes the month first. Everywhere else in the
 * trade lanes this product covers writes the day first, so month-first is the
 * exception list rather than the default.
 */
const MONTH_FIRST_CURRENCIES = new Set(['USD', 'PHP']);
const ENGLISH_SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export type DateOrder = 'dmy' | 'mdy';

/** Which way round a date is written, given the money it sits beside. */
export function getDateOrder(currency?: string | null): DateOrder {
  if (!currency) return 'dmy';

  return MONTH_FIRST_CURRENCIES.has(normalizeCurrencyCode(currency))
    ? 'mdy'
    : 'dmy';
}

export function getDatePlaceholder(order: DateOrder) {
  return order === 'mdy' ? 'mm/dd/yyyy' : 'dd/mm/yyyy';
}

export function dateToIso(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

/** `2026-09-03` becomes `03/09/2026`, or `09/03/2026` when month-first. */
export function isoToDisplay(iso: string, order: DateOrder) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());

  if (!match) return '';

  const [, year, month, day] = match;

  return order === 'mdy'
    ? `${month}/${day}/${year}`
    : `${day}/${month}/${year}`;
}

/**
 * Formats the calendar portion of an ISO timestamp without consulting the
 * runtime's locale or time zone. This is safe to render on both the server and
 * client during hydration.
 */
export function isoToEnglishPublishedDate(iso: string) {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})?)?$/.exec(
      iso.trim(),
    );

  if (!match) return '';

  const [, year, rawMonth, rawDay] = match;
  const monthIndex = Number(rawMonth) - 1;
  const month = ENGLISH_SHORT_MONTHS[monthIndex];
  const day = Number(rawDay);
  const yearNumber = Number(year);
  const candidate = new Date(Date.UTC(yearNumber, monthIndex, day));

  if (
    !month ||
    candidate.getUTCFullYear() !== yearNumber ||
    candidate.getUTCMonth() !== monthIndex ||
    candidate.getUTCDate() !== day
  ) {
    return '';
  }

  return `${month} ${day}, ${year}`;
}

/**
 * The typed text back to an ISO date, or `null` while it is still incomplete.
 *
 * Separators are deliberately loose, because people paste `3.9.2026` and
 * `3-9-26` out of carrier emails, but the field order is not: a two-digit
 * first part is read as the day under `dmy` even when it could be a month.
 * Guessing per value is how `03/09` becomes March in one row and September in
 * the next.
 */
export function displayToIso(input: string, order: DateOrder): string | null {
  const parts = input
    .trim()
    .split(/[^\d]+/)
    .filter(Boolean);

  if (parts.length !== 3) return null;

  const [first, second, rawYear] = parts as [string, string, string];

  if (rawYear.length !== 2 && rawYear.length !== 4) return null;

  const day = Number(order === 'mdy' ? second : first);
  const month = Number(order === 'mdy' ? first : second);

  let year = Number(rawYear);

  if (rawYear.length === 2) year += year < 70 ? 2000 : 1900;

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const candidate = new Date(year, month - 1, day);

  /*
   * A `Date` rolls 31 February forward into March rather than rejecting it, so
   * the only reliable check is whether the date it produced is still the date
   * that was asked for.
   */
  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }

  return dateToIso(candidate);
}

/** An ISO date read as calendar-local, so a UTC parse cannot shift the day. */
export function isoToDate(iso: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());

  if (!match) return undefined;

  const [, year, month, day] = match;

  return new Date(Number(year), Number(month) - 1, Number(day));
}
